import { useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "../localization/useTranslation";
import { useUrlProvider } from "../useUrlProvider";
import { RoleViewModel } from "./Roles.types";
import { DotLegalBreadCrumbModel } from "@dotlegal/dotlegal-ui-components";
import { FilterOption } from "../common/components/filterPopover/FilterPopover.types";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider.tsx";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";

export function useRolesHooks() {
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();
    const { id } = useParams<{ id: string }>();
    const { roleid } = useParams<{ roleid: string }>();
    const [showRolesDialog, setShowRolesDialog] = useState(false);
    const [rolePermission, setRolePermissions] = useState<RoleViewModel | undefined>(undefined);
    const [roleForDeletion, setRoleForDeletion] = useState<RoleViewModel | undefined>(undefined);
    const [roleForEditing, setRoleForEditing] = useState<RoleViewModel | undefined>(undefined);
    const [roleForStatusChange, setRoleForStatusChange] = useState<RoleViewModel | undefined>(undefined);

    const { customerId } = useUserContext();

    const sessionKey = `roles-overview-${customerId}`;
    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(sessionKey);
    const isActive = selectFilters.active ?? (sessionStorage.getItem(sessionKey) ? [] : ["true"]);
    const isStandardRole = selectFilters.standardRole ?? [];

    let { isLoading, data, refetch: refetchData } = useDotLegalQuery<Array<RoleViewModel>>("/roles");

    const hasRoles = isLoading || data!.length > 0;

    const roles = applyClientFilters(
        data ?? [],
        [
            { id: "active", candidate: (r) => r.isActive.toString() },
            { id: "standardRole", candidate: (r) => r.isStandardRole.toString() },
        ],
        { ...selectFilters, active: isActive, standardRole: isStandardRole },
        {},
        filterOperators
    )!;

    const getBreadCrumbs = () => {
        let breadCrumbs: Array<DotLegalBreadCrumbModel> = [];

        breadCrumbs.push({ name: translateString("roles") });

        return breadCrumbs;
    };

    function getFilterDataOptions() {
        if (!data) {
            return [];
        }

        return withOperators(
            [
                {
                    type: 1,
                    id: "active",
                    value: isActive,
                    label: translateString("status"),
                    fixedData: [
                        { id: "true", name: translateString("active") },
                        { id: "false", name: translateString("deactivated") },
                    ],
                },
                {
                    type: 1,
                    id: "standardRole",
                    value: isStandardRole,
                    label: translateString("standardRole"),
                    fixedData: [
                        { id: "true", name: translateString("yes") },
                        { id: "false", name: translateString("no") },
                    ],
                },
            ],
            filterOperators
        );
    }

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        data: roles,
        refetchData,
        roleForEditing,
        setRoleForEditing,
        showRolesDialog,
        setShowRolesDialog,
        hasRoles,
        id,
        roleid,
        getBreadCrumbs,
        roleForStatusChange,
        setRoleForStatusChange,
        getFilterDataOptions,
        mapSearchFilter: onFilterChange,
        roleForDeletion,
        setRoleForDeletion,
        rolePermission,
        setRolePermissions,
    };
}
